"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  HomeIcon,
  InformationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

import { Badge } from "@/components/ui/badge";
import { Divider } from "@/components/ui/divider";
import { Button } from "@/components/ui/button";
import { Consultation } from "@/app/_components/consultation-card";
import { PrintButton } from "@/app/_components/print-button";
import { Error } from "@/app/_components/error";
import { Loading } from "./loading";

import { useAnalyze } from "@/queries/use-analyze";

import { formatBRL } from "@/helper/currency";
import { emailSupport, phoneSupport } from "@/constants";

import type { AnalyzeFilter } from "@/types";

interface RegistryProps extends AnalyzeFilter {
  hidePropertyButton?: boolean;
  emptyElement?: React.ReactNode;
}

export const Registry = ({
  hidePropertyButton,
  emptyElement,
  id,
  field,
}: RegistryProps) => {
  const { data, isPending, error, refetch } = useAnalyze({ id, field });

  const property = data?.propriedade;
  const analyze = data?.analise;

  const analyzeData = analyze?.data;
  const hasValidData = analyzeData && property;

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  if (!hasValidData) {
    return (
      emptyElement ?? (
        <>
          <div className="flex flex-col gap-3 items-center justify-center">
            <h2 className="text-center text-base text-muted-foreground w-4/6">
              A análise da matrícula provavelmente não está disponível. Se você
              tiver alguma dúvida, não hesite em entrar em contato conosco.
            </h2>
          </div>
          <div className="flex items-center justify-center gap-4">
            <div>
              <Button asChild variant="outline">
                <a
                  href={`mailto:${emailSupport}?subject=Pro Leilão - Análise de matrícula não encontrado`}
                  target="_blank"
                  rel="nofollow"
                >
                  Suporte via Email
                </a>
              </Button>
            </div>
            <div>
              <Button asChild variant="outline">
                <a
                  href={`https://wa.me/${phoneSupport}?text=Olá,%20estou%20entrando%20em%20contato%20atráves%20do%20Pro%20Leilão...`}
                  target="_blank"
                  rel="nofollow"
                >
                  Suporte via WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </>
      )
    );
  }

  return (
    <>
      <div className="flex justify-end items-center gap-2">
        <PrintButton />
        {!hidePropertyButton ? (
          <Button asChild className="print:hidden">
            <a
              href={`/leilao-de-imoveis/${property.uf}/imovel/${property.numero_do_leilao}`}
              target="_blank"
            >
              Ver página do imóvel
            </a>
          </Button>
        ) : null}
      </div>
      <Card className="bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center">
            <InformationCircleIcon className="mr-2 w-4 h-4" /> Resumo
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium">Endereço</p>
              <p className="text-lg">
                {analyzeData.imovel?.endereco || "Não informado"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Área Total</p>
              <p className="text-lg">
                {analyzeData.imovel?.area_total || "Não informado"} m²
              </p>
            </div>
            <div>
              <p className="text-sm font-medium">Valor Atual</p>
              <p className="text-lg">
                {analyzeData.info?.consolidacao?.valor
                  ? formatBRL(analyzeData.info.consolidacao.valor)
                  : "Não informado"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Consultation.Card
        title="Detalhes do Imóvel"
        description="Informações detalhadas sobre o imóvel"
        icon={<HomeIcon className="mr-2 w-4 h-4" />}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Área Privativa</p>
            <p className="text-lg font-medium">
              {analyzeData.imovel?.area_privativa || "Não informado"} m²
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Área Comum</p>
            <p className="text-lg font-medium">
              {analyzeData.imovel?.area_comum || "Não informado"} m²
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Área Total</p>
            <p className="text-lg font-medium">
              {analyzeData.imovel?.area_total || "Não informado"} m²
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              Inscrição Imobiliária
            </p>
            <p className="text-lg font-medium break-words">
              {analyzeData.imovel?.inscricao_imobiliaria || "Não informado"}
            </p>
          </div>
        </div>
      </Consultation.Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Consultation.Card
          title="Moradores"
          description="Informações detalhadas sobre os moradores do imóvel"
          icon={<UserIcon className="mr-2 w-4 h-4" />}
        >
          <div className="flex flex-col gap-6">
            {analyzeData.moradores && analyzeData.moradores.length > 0 ? (
              <div className="flex flex-col gap-2">
                <h3 className="font-medium">Morador Anterior:</h3>
                <ul className="flex flex-col gap-4">
                  {analyzeData.moradores.map((morador) => (
                    <div key={morador.nome} className="flex flex-col gap-2">
                      <li>
                        <p>{morador.nome || "Não informado"}</p>
                      </li>
                      <li>
                        <p className="text-sm text-muted-foreground capitalize">
                          Profissão: {morador.profissão || "Não informado"}
                        </p>
                      </li>
                      <li>
                        <p className="text-sm text-muted-foreground">
                          CPF: {morador.cpf || "Não informado"}
                        </p>
                      </li>
                      <li>
                        <p className="text-sm text-muted-foreground capitalize">
                          Estado Civil:{" "}
                          {morador.estado_civil || "Não informado"}
                        </p>
                      </li>
                      <li>
                        <p className="text-sm text-muted-foreground capitalize">
                          Casa Anterior:{" "}
                          {morador.casa_anterior || "Não informado"}
                        </p>
                      </li>
                      <li>
                        <p className="text-sm text-muted-foreground capitalize">
                          Data de Nascimento:{" "}
                          {morador.data_nascimento || "Não informado"}
                        </p>
                      </li>
                      <Divider size="large" borderWidth={1} />
                    </div>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <h3 className="font-medium">Morador Anterior:</h3>
                <p>Não informado</p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <h3 className="font-medium">Atual Morador:</h3>
              <p>{analyzeData.atual_morador?.nome || "Não informado"}</p>
              <p className="text-sm text-muted-foreground capitalize">
                Tipo: {analyzeData.atual_morador?.tipo || "Não informado"}
              </p>
              <p className="text-sm text-muted-foreground">
                CNPJ: {analyzeData.atual_morador?.cnpj || "Não informado"}
              </p>
            </div>
          </div>
        </Consultation.Card>
        <Consultation.Card
          title="Compra e Avaliação"
          description="Informações sobre a compra e avaliação do imóvel"
          icon={<CurrencyDollarIcon className="mr-2 w-4 h-4" />}
        >
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-medium">Valor de Compra:</h3>
              <p>
                Proprietário:{" "}
                {analyzeData.info?.primeira_compra?.proprietario ||
                  "Não informado"}
              </p>
              <p>
                {analyzeData.info?.primeira_compra?.valor
                  ? formatBRL(analyzeData.info.primeira_compra.valor)
                  : "Não informado"}
              </p>
              <p className="text-sm text-muted-foreground">
                Data:{" "}
                {analyzeData.info?.primeira_compra?.data || "Não informado"}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="font-medium">Valor Atual:</h3>
              <p>
                {analyzeData.info?.consolidacao?.valor
                  ? formatBRL(analyzeData.info.consolidacao.valor)
                  : "Não informado"}
              </p>
              <p className="text-sm text-muted-foreground">
                Data: {analyzeData.info?.consolidacao?.data || "Não informado"}
              </p>
            </div>
          </div>
        </Consultation.Card>
      </div>
      <Consultation.Card
        title="Detalhes do Financiamento"
        description="Informações detalhadas sobre o financiamento do imóvel"
        icon={<CreditCardIcon className="mr-2 w-4 h-4" />}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h3 className="font-medium">Financiamento Original:</h3>
            <p className="text-sm text-muted-foreground">
              Valor:{" "}
              <span className="text-lg font-medium text-neutral-800">
                {analyzeData.info?.financiamento?.valor
                  ? formatBRL(analyzeData.info.financiamento.valor)
                  : "Não informado"}
              </span>
            </p>
            <p className="text-sm text-muted-foreground">
              Instituição:{" "}
              {analyzeData.info?.financiamento?.instituicao || "Não informado"}
            </p>
            <p className="text-sm text-muted-foreground">
              Prazo: {analyzeData.info?.financiamento?.prazo || "Não informado"}
            </p>
            <p className="text-sm text-muted-foreground">
              Taxa de juros anual:{" "}
              {analyzeData.info?.financiamento?.juros || "Não informado"}
              {analyzeData.info?.financiamento?.juros ? "%" : ""}
            </p>
            {analyzeData.info?.financiamento?.programa ? (
              <div>
                <Badge>{analyzeData.info.financiamento.programa}</Badge>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="font-medium">Detalhes do Financiamento:</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  Recursos Próprios
                </p>
                <p className="text-lg font-medium">
                  {analyzeData.info?.detalhes?.recursos_proprios
                    ? formatBRL(analyzeData.info.detalhes.recursos_proprios)
                    : "Não informado"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recursos FGTS</p>
                <p className="text-lg font-medium">
                  {analyzeData.info?.detalhes?.recursos_fgts
                    ? formatBRL(analyzeData.info.detalhes.recursos_fgts)
                    : "Não informado"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Desconto Complementar
                </p>
                <p className="text-lg font-medium">
                  {analyzeData.info?.detalhes?.desconto
                    ? formatBRL(analyzeData.info.detalhes.desconto)
                    : "Não informado"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Parcela Mensal Inicial
                </p>
                <p className="text-lg font-medium">
                  {analyzeData.info?.detalhes?.parcela
                    ? formatBRL(analyzeData.info.detalhes.parcela)
                    : "Não informado"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Consultation.Card>
      <Consultation.Card
        title="Irregularidades"
        description="Informações detalhadas sobre as irregularidades do imóvel contidas na matrícula"
        icon={<InformationCircleIcon className="mr-2 w-4 h-4" />}
      >
        {analyzeData.irregularidades &&
        analyzeData.irregularidades.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {analyzeData.irregularidades.map((irregularidade, index) => (
              <li key={index}>{irregularidade}</li>
            ))}
          </ul>
        ) : (
          <p className="text-green-600">Nenhuma irregularidade encontrada.</p>
        )}
      </Consultation.Card>
    </>
  );
};
